// Утилиты форматирования курсов и сумм для crypto-swap-widget.
//
// Задача — показать пользователю числа компактно и без «мусорных»
// нулей, но с достаточной точностью для крипто-курсов, где значения
// могут отличаться на 8 порядков (BTC vs sat, USDT vs 1e-8 BTC).

/**
 * Форматирует decimal-строку (или number) для отображения в UI.
 *
 * Правила:
 *   • ≥ 1000       → 2 знака после запятой + separator тысяч
 *   • ≥ 1          → до 4 знаков, обрезаем хвостовые нули
 *   • ≥ 0.01       → до 6 знаков, обрезаем хвостовые нули
 *   • ≥ 0.0001     → до 8 знаков, обрезаем хвостовые нули
 *   • < 0.0001     → 4 значимые цифры (сохраняем точность на очень
 *                    мелких значениях, но без 20 нулей подряд)
 *   • ноль/пусто   → "0"
 */
export function formatAmount(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "0";
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n) || n === 0) return "0";

  const abs = Math.abs(n);
  let out: string;

  if (abs >= 1000) {
    out = n.toLocaleString("ru-RU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } else if (abs >= 1) {
    out = trimTrailing(n.toFixed(4));
  } else if (abs >= 0.01) {
    out = trimTrailing(n.toFixed(6));
  } else if (abs >= 0.0001) {
    out = trimTrailing(n.toFixed(8));
  } else {
    // 4 значимых цифры — например 0.00001568.
    out = trimTrailing(n.toPrecision(4));
    // toPrecision может дать научную запись — на всякий случай
    // конвертируем.
    if (/e/i.test(out)) {
      out = trimTrailing(Number(out).toFixed(20));
    }
  }
  return out;
}

/**
 * Формат курса «1 X ≈ ... Y». Просто shortcut над `formatAmount`.
 */
export function formatRate(
  from: string | null | undefined,
  to: string | null | undefined,
  rate: string | number | null | undefined,
): string {
  if (!from || !to || rate === null || rate === undefined || rate === "") return "";
  return `1 ${from} ≈ ${formatAmount(rate)} ${to}`;
}

function trimTrailing(s: string): string {
  if (!s.includes(".")) return s;
  return s.replace(/\.?0+$/, "");
}
